import { Button, FestiveButton, HeadingTag, IconRenderer, ModalPortal, ResponsiveImage } from '@/components';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import Close from '@/assets/close3.svg';
import { useSelector } from 'react-redux';
import { selectCampaignIcon, selectCampaignId, selectIsCampaignActive } from '@/store/index';
import { useRouter } from 'next/router';

const getCookie = (name) => {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? match[2] : null;
};

const normalize = (str) =>
  str
    ?.toLowerCase()
    .replace(/<\/?p\b[^>]*>/gi, '')
    .replace(/\s+/g, '_');

const FullscreenPopup = ({ data, ...props }) => {
  const [campaignId, campaignActive] = [useSelector(selectCampaignId), useSelector(selectIsCampaignActive)];
  const [open, setOpen] = useState(true);
  const normalizedHeading = normalize(data.heading?.heading);
  const router = useRouter();

  const setCookie = (campaignId) => {
    const date = new Date();
    date.setTime(date.getTime() + 24 * 60 * 60 * 1000);

    document.cookie = `fs_popup_closed_${normalizedHeading}=${campaignId};expires=${date.toUTCString()};path=/`;
  };

  useEffect(() => {
    const popupCookie = getCookie(`fs_popup_closed_${normalizedHeading}`);

    setOpen(!popupCookie || (popupCookie && String(popupCookie).toLowerCase() !== String(campaignId).toLowerCase()));
  }, [campaignId, normalizedHeading]);

  useEffect(() => {
    document.body.style.overflowY = open ? 'scroll' : '';
    document.body.style.position = open ? 'fixed' : 'static';
  }, [open]);

  const handleClose = () => {
    setOpen(false);
    setCookie(campaignId);
  };

  const handleButtonClick = (e) => {
    e.preventDefault();

    const href = e.target.href;

    handleClose();

    if (href) {
      router.push(href);
    }
  };

  const variants = {
    initial: {
      opacity: 0,
    },
    active: {
      opacity: 1,
      transition: {
        type: 'spring',
        damping: 30,
        stiffness: 120,
        // delay: (animationCookie ? 0 : 7) + data.delay,
      },
    },
    exit: {
      opacity: 0,
      transition: {
        type: 'spring',
        damping: 30,
        stiffness: 120,
      },
    },
  };

  return (
    <ModalPortal>
      <AnimatePresence>
        {open && (
          <motion.div
            className="FullscreenPopup inset-0 fixed z-50 bg-primary/70"
            {...props}
            aria-modal="true"
            role="dialog"
            variants={variants}
            initial="initial"
            animate="active"
            exit="exit"
          >
            <div className="relative z-10 w-full lg:aspect-[1128/613] aspect-[343/583] md:aspect-[688/613] max-h-[583px] md:max-h-[613px] lg:max-h-[none] flex flex-col justify-center max-w-[calc(100%-32px)] md:max-w-[calc(100%-80px)] lg:max-w-[min(1128px,calc(100%-80px))] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
              <button
                className="absolute lg:top-3 lg:right-3 top-2.5 right-2.5 md:top-4 md:right-4 z-10 flex items-center justify-center size-8 md:size-10 lg:size-16 bg-white"
                onClick={handleClose}
              >
                <Close role="presentation" aria-label="Close" className="size-3 md:size-6 fill-black" />
              </button>

              <ResponsiveImage
                image={data.backgroundImage}
                widths={{ xs: 1128, sm: 1128, md: 1128, lg: 1128, xl: 1128, xxl: 1128 }}
                heights={{ xs: 613, sm: 613, md: 613, lg: 613, xl: 613, xxl: 613 }}
                className="absolute z-[1] inset-0 before:content-[''] before:absolute before:inset-0 before:bg-black/50"
              />

              <div className="w-full h-fit flex relative z-[2] flex-col px-[9px] md:px-0 items-center text-white gap-6 md:max-w-[550px] lg:max-w-[875px] mx-auto">
                {campaignActive && data.campaignMode && (
                  <IconRenderer selector={selectCampaignIcon} className="fill-current size-6" />
                )}
                {data.preHeading && (
                  <p className="text-paragraph-m leading-6 -tracking-[0.02em] text-center font-cera">
                    {data.preHeading}
                  </p>
                )}
                {data.heading && data.heading.heading !== '' && (
                  <HeadingTag
                    data={data.heading}
                    className="text-headingReg -tracking-[0.01em] md:text-title-l md:leading-60 md:-tracking-[0.03em] font-ivy-ora text-center"
                  />
                )}
                {data.description && (
                  <p className="text-large-paragraph font-cera text-center md:max-w-[400px]">{data.description}</p>
                )}
                {(data.cta1 || data.cta2 || data.cta3) && (
                  <div className="flex items-center gap-4 md:gap-6 flex-wrap justify-center">
                    {data.cta1 ? (
                      campaignActive && data.campaignMode ? (
                        <FestiveButton
                          link={data.cta1}
                          onClick={handleButtonClick}
                          className="[&>*]:pointer-events-none"
                        />
                      ) : (
                        <Button link={data.cta1} type="primary" theme="light" onClick={handleButtonClick} />
                      )
                    ) : null}
                    {data.cta2 ? (
                      campaignActive && data.campaignMode ? (
                        <FestiveButton
                          link={data.cta2}
                          onClick={handleButtonClick}
                          className="[&>*]:pointer-events-none"
                        />
                      ) : (
                        <Button link={data.cta2} type="primary" theme="light" onClick={handleButtonClick} />
                      )
                    ) : null}
                    {data.cta3 ? (
                      campaignActive && data.campaignMode ? (
                        <FestiveButton
                          link={data.cta3}
                          onClick={handleButtonClick}
                          className="[&>*]:pointer-events-none"
                        />
                      ) : (
                        <Button link={data.cta3} type="primary" theme="light" onClick={handleButtonClick} />
                      )
                    ) : null}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </ModalPortal>
  );
};

export default FullscreenPopup;
